/**
 * Utility functions to parse contentJson with the new nested structure
 * Structure: Landing Page -> fields -> components -> panel
 */

import logger from '@/utils/logger';

/**
 * Extract panel data from contentJson by panel key
 * @param contentJson The full contentJson array
 * @param panelKey The key of the panel to extract (e.g., "highImpactModelPanel")
 * @param pageName The name of the page (default: "Landing Page")
 * @returns The panel object or null if not found
 */
export const extractPanelData = (
  contentJson: any[],
  panelKey: string,
  pageName: string = "Landing Page"
): any | null => {
  try {
    const page = contentJson?.find((p: any) => p.name === pageName);
    if (!page || !Array.isArray(page.fields)) return null;

    // Iterate through fields to find the panel
    for (const field of page.fields) {
      if (field.components && Array.isArray(field.components)) {
        for (const component of field.components) {
          if (component.type === "panel" && component.key === panelKey) {
            return component;
          }
        }
      }
    }
    return null;
  } catch (error) {
    logger.error(`Error extracting panel data for ${panelKey}:`, error);
    return null;
  }
};

/**
 * Get field value from panel components by field key
 * @param panel The panel object
 * @param fieldKey The key of the field to extract
 * @param defaultValue Default value if field is not found
 * @returns The field's defaultValue or the provided defaultValue
 */
export const getFieldValue = (
  panel: any,
  fieldKey: string,
  defaultValue: any = null
): any => {
  try {
    if (!panel || !Array.isArray(panel.components)) return defaultValue;
    const field = panel.components.find((comp: any) => comp.key === fieldKey);
    return field?.defaultValue ?? defaultValue;
  } catch (error) {
    logger.error(`Error getting field value for ${fieldKey}:`, error);
    return defaultValue;
  }
};

/**
 * Get nested panel from within another panel (for sub-panels like card1Panel, card2Panel, etc.)
 * @param parentPanel The parent panel object
 * @param nestedPanelKey The key of the nested panel
 * @returns The nested panel object or null
 */
export const getNestedPanel = (
  parentPanel: any,
  nestedPanelKey: string
): any | null => {
  try {
    if (!parentPanel || !Array.isArray(parentPanel.components)) return null;
    const nestedPanel = parentPanel.components.find(
      (comp: any) => comp.type === "panel" && comp.key === nestedPanelKey
    );
    return nestedPanel || null;
  } catch (error) {
    logger.error(`Error getting nested panel for ${nestedPanelKey}:`, error);
    return null;
  }
};

/**
 * Fetch contentJson from the server
 * @returns Promise with contentJson data or empty array on error
 */
export const fetchContentJson = async (): Promise<any[]> => {
  try {
    const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error fetching contentJson:", error);
    return [];
  }
};

/**
 * Extract multiple field values from a panel at once
 * @param panel The panel object
 * @param fieldKeys Array of field keys to extract
 * @returns Object with field keys mapped to their values
 */
export const getMultipleFieldValues = (
  panel: any,
  fieldKeys: string[]
): Record<string, any> => {
  const result: Record<string, any> = {};
  fieldKeys.forEach((key) => {
    result[key] = getFieldValue(panel, key, "");
  });
  return result;
};
